'use client'

import { useEffect } from 'react'

/**
 * Self-healing guard for the stranded Radix body lock.
 *
 * When stacked modal layers (Dialog / AlertDialog / dropdowns) close in the
 * same tick, Radix's cleanup can leave `pointer-events: none` on `<body>`:
 * the page renders but ignores every click. The known sites are fixed with
 * unwindDialogStack(), but the app has ~17 ConfirmDialog sites and several
 * dialog-in-dialog flows — this guard makes the failure self-repairing
 * everywhere: on the first pointerdown after a strand, if the body is locked
 * while NO Radix layer is open, clear the lock.
 *
 * The layer check is deliberately broad (dialog, alertdialog, menu,
 * listbox) and the failure direction is safe: a missed match means the
 * guard does nothing on that click and heals on a later one — it can never
 * unlock the body while something is legitimately open.
 *
 * Window-level capture listener on purpose: with the body locked, no
 * element in the body subtree receives events, but window still does.
 */
export function DialogLockGuard() {
  useEffect(() => {
    const heal = () => {
      if (document.body.style.pointerEvents !== 'none') return
      const openLayer = document.querySelector(
        '[role="dialog"][data-state="open"], [role="alertdialog"][data-state="open"], [role="menu"][data-state="open"], [role="listbox"][data-state="open"]'
      )
      if (!openLayer) {
        document.body.style.pointerEvents = ''
      }
    }
    window.addEventListener('pointerdown', heal, { capture: true })
    return () => window.removeEventListener('pointerdown', heal, { capture: true })
  }, [])

  return null
}
